<ng-template #mainContentTpl>
  <div
    class="dynamic-page-container w-full relative [perspective:2000px] print:![perspective:none]"
    [ngClass]="expandToContentHeight() ? 'h-auto' : 'h-full'"
    (click)="handleUserInteraction($event)"
    (input)="handleInputInteraction($event)"
    (change)="handleInputInteraction($event)"
    (state-change)="handleCustomElementStateChange($event)"
  >
    <div
      class="relative w-full transition-transform duration-700 [transform-style:preserve-3d] print:!transform-none print:![transform-style:flat]"
      [ngClass]="expandToContentHeight() ? 'h-auto' : 'h-full'"
      [style.transform]="
        dynamicPageUiService.isCodeViewActive()
          ? 'rotateY(180deg)'
          : 'rotateY(0deg)'
      "
    >
      <div
        class="relative w-full transition-transform duration-700 [transform-style:preserve-3d] print:!transform-none print:![transform-style:flat]"
        [ngClass]="expandToContentHeight() ? 'h-auto' : 'h-full'"
        [style.transform]="
          dynamicPageUiService.isCodeViewActive()
            ? 'rotateY(180deg)'
            : 'rotateY(0deg)'
        "
      >
        <div
          class="w-full [backface-visibility:hidden] bg-body-background flex flex-col z-10 print:!relative print:!h-auto print:!overflow-visible"
          [ngClass]="
            expandToContentHeight()
              ? 'relative h-auto'
              : 'absolute inset-0 h-full'
          "
        >
          @if (isLoading()) {
            <div
              class="flex items-center justify-center p-12 w-full min-h-[400px]"
              [ngClass]="expandToContentHeight() ? 'h-auto' : 'h-full'"
            >
              <div class="flex flex-col items-center gap-4">
                <i
                  class="fas fa-circle-notch fa-spin text-5xl text-primary"
                ></i>
                <span class="text-secondary-text font-medium text-lg">{{
                  configTexts.loading
                }}</span>
              </div>
            </div>
          } @else if (error()) {
            <div
              class="flex items-center justify-center w-full min-h-[400px]"
              [ngClass]="expandToContentHeight() ? 'h-auto' : 'h-full'"
            >
              <div
                class="p-8 text-center text-danger bg-danger/5 border border-danger/20 rounded-xl m-4 max-w-md shadow-sm flex flex-col items-center"
              >
                <i class="fas fa-exclamation-triangle text-4xl mb-3"></i>
                <h3 class="text-xl font-bold mb-2">
                  {{ configTexts.errorPrefix }}
                </h3>
                <p class="font-medium text-danger/80 mb-6">{{ error() }}</p>

                <button
                  (click)="loadPipeline()"
                  class="px-6 py-2.5 bg-component-inner text-primary-text rounded-lg shadow-sm border border-secondary-border hover:bg-hover-default hover:text-primary transition-colors focus:outline-none flex items-center gap-2"
                >
                  <i class="fas fa-sync-alt"></i>
                  <span>{{ configTexts.retryButton }}</span>
                </button>
              </div>
            </div>
          } @else {
            <div
              class="w-full animate-fade-in relative pb-16 print:pb-0"
              [ngClass]="
                expandToContentHeight()
                  ? 'h-auto overflow-visible'
                  : 'flex-1 min-h-0 overflow-y-auto custom-scrollbar'
              "
            >
              @if (isRefreshing()) {
                <div
                  class="absolute top-4 right-4 bg-component-inner border border-primary-border text-primary text-xs px-3 py-1.5 rounded-full shadow-md flex items-center gap-2 z-50 animate-pulse font-bold"
                >
                  <i class="fas fa-sync fa-spin"></i> Updating...
                </div>
              }

              <rapider-template-renderer
                [template]="activeTemplate()"
                [data]="templateContext()"
                [simulateViewMode]="simulateViewMode()"
              >
              </rapider-template-renderer>
            </div>
          }
        </div>

        <div
          class="absolute !p-4 inset-0 w-full h-full [backface-visibility:hidden] [transform:rotateY(180deg)] bg-component-inner border border-primary-border shadow-sm flex flex-col overflow-hidden z-0 print:hidden"
        >
          <nz-tabs class="h-full w-full flex-1 flex flex-col" nzSize="large">
            <nz-tab [nzTitle]="configTexts.tabPageConfig">
              <div class="h-[calc(100vh-200px)] w-full p-2 pb-12">
                <rapider-monaco-code-editor
                  [title]="configTexts.tabPageConfig"
                  language="json"
                  [code]="pageConfigStringified()"
                  (codeChange)="handlePageConfigChange($event)"
                >
                </rapider-monaco-code-editor>
              </div>
            </nz-tab>
            <nz-tab [nzTitle]="configTexts.tabHtml">
              <div class="h-[calc(100vh-200px)] w-full p-2 pb-12">
                <rapider-monaco-code-editor
                  [title]="configTexts.tabHtml"
                  language="html"
                  [code]="activeTemplate()"
                  (codeChange)="handleTemplateChange($event)"
                >
                </rapider-monaco-code-editor>
              </div>
            </nz-tab>

            <nz-tab [nzTitle]="configTexts.tabData">
              <div class="h-[calc(100vh-200px)] w-full p-2 pb-12">
                <rapider-monaco-code-editor
                  [title]="configTexts.tabData"
                  language="json"
                  [code]="templateContextStringified()"
                  (codeChange)="handleDataChange($event)"
                >
                </rapider-monaco-code-editor>
              </div>
            </nz-tab>
          </nz-tabs>
        </div>
      </div>
    </div>
  </div>
</ng-template>

@if (isPageSplitterVisible()) {
  <nz-splitter
    [nzLayout]="pageSplitterLayout()"
    class="w-full h-full bg-body-background"
    [ngClass]="
      !pageSplitterResizable()
        ? '[&_.ant-splitter-bar]:pointer-events-none'
        : ''
    "
  >
    <nz-splitter-panel>
      <ng-container *ngTemplateOutlet="mainContentTpl"></ng-container>
    </nz-splitter-panel>

    <nz-splitter-panel
      [nzDefaultSize]="pageSplitterDefaultSize()"
      [nzMin]="pageSplitterMin()"
      [nzMax]="pageSplitterMax()"
    >
      <div
        class="flex flex-col h-full w-full bg-body-background border-l border-primary-border overflow-hidden @container"
      >
        <header
          class="flex items-center justify-between p-3 border-b border-secondary-border bg-component-header shrink-0"
        >
          <div class="flex items-center gap-2">
            @if (pageSplitterIcon()) {
              <i [class]="pageSplitterIcon() + ' text-secondary-text'"></i>
            }
            @if (pageSplitterTitle()) {
              <span class="font-bold text-sm text-primary-text">{{
                pageSplitterTitle()
              }}</span>
            }
          </div>
          <button
            (click)="closePageSplitter()"
            class="w-6 h-6 rounded flex items-center justify-center text-secondary-text hover:text-danger hover:bg-danger/10 focus:outline-none transition-colors"
          >
            <i class="fas fa-times"></i>
          </button>
        </header>
        <div
          class="flex-1 min-h-0 w-full relative overflow-y-auto custom-scrollbar"
        >
          @if (isPageSplitterLoading()) {
            <div class="flex items-center justify-center p-12 w-full h-full">
              <div class="flex flex-col items-center gap-4">
                <i
                  class="fas fa-circle-notch fa-spin text-4xl text-primary"
                ></i>
                <span class="text-secondary-text font-medium text-base"
                  >Loading pane...</span
                >
              </div>
            </div>
          } @else if (pageSplitterConfig()) {
            <rapider-dynamic-page-renderer
              [pageConfig]="pageSplitterConfig()!"
              [manifestPages]="manifestPages()"
              [appId]="appId()"
              [appDefinition]="appDefinition()"
              [initialPayload]="pageSplitterPayload()"
              [standaloneMode]="true"
              [expandToContentHeight]="true"
            >
            </rapider-dynamic-page-renderer>
          }
        </div>
      </div>
    </nz-splitter-panel>
  </nz-splitter>
} @else {
  <ng-container *ngTemplateOutlet="mainContentTpl"></ng-container>
}

<nz-modal
  [nzVisible]="isPageModalVisible()"
  (nzVisibleChange)="isPageModalVisible.set($event)"
  [nzTitle]="pageModalTitle()"
  [nzWidth]="pageModalWidth()"
  [nzFooter]="null"
  (nzOnCancel)="closePageModal()"
>
  <ng-container *nzModalContent>
    @if (isPageModalLoading()) {
      <div class="flex items-center justify-center p-12 w-full min-h-[300px]">
        <div class="flex flex-col items-center gap-4">
          <i class="fas fa-circle-notch fa-spin text-4xl text-primary"></i>
          <span class="text-secondary-text font-medium text-base"
            >Loading modal...</span
          >
        </div>
      </div>
    } @else if (pageModalConfig()) {
      <div
        class="w-full relative max-h-[80vh] overflow-y-auto custom-scrollbar"
      >
        <rapider-dynamic-page-renderer
          [pageConfig]="pageModalConfig()!"
          [manifestPages]="manifestPages()"
          [appId]="appId()"
          [appDefinition]="appDefinition()"
          [initialPayload]="pageModalPayload()"
          [standaloneMode]="true"
          [expandToContentHeight]="true"
        >
        </rapider-dynamic-page-renderer>
      </div>
    }
  </ng-container>
</nz-modal>

<nz-drawer
  #pageDrawer="nzDrawer"
  [nzVisible]="isPageDrawerVisible()"
  (nzVisibleChange)="isPageDrawerVisible.set($event)"
  [nzTitle]="pageDrawerTitle()"
  [nzPlacement]="pageDrawerPlacement()"
  [nzWidth]="effectiveDrawerWidth()"
  (nzOnClose)="closePageDrawer()"
>
  <ng-container *nzDrawerContent>
    @if (isPageDrawerLoading()) {
      <div class="flex items-center justify-center p-12 w-full h-full">
        <div class="flex flex-col items-center gap-4">
          <i class="fas fa-circle-notch fa-spin text-4xl text-primary"></i>
          <span class="text-secondary-text font-medium text-base"
            >Loading drawer...</span
          >
        </div>
      </div>
    } @else if (pageDrawerConfig()) {
      <div class="w-full h-full relative overflow-y-auto custom-scrollbar">
        <rapider-dynamic-page-renderer
          [pageConfig]="pageDrawerConfig()!"
          [manifestPages]="manifestPages()"
          [appId]="appId()"
          [appDefinition]="appDefinition()"
          [initialPayload]="pageDrawerPayload()"
          [standaloneMode]="true"
          [expandToContentHeight]="true"
        >
        </rapider-dynamic-page-renderer>
      </div>
    }
  </ng-container>
</nz-drawer>
